import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import { ConfirmModal } from '../../components/ConfirmModal';
import { Layout } from '../../components/Layout';
import { useAuth } from '../../contexts/AuthContext';

interface ClientOption {
  id: string;
  name: string;
}

function tsToMs(ts: any): number {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

function fmtDaysLeft(ts: any): string | null {
  const ms = tsToMs(ts);
  if (!ms) return null;
  const d = Math.ceil((ms - Date.now()) / (24 * 60 * 60 * 1000));
  if (d < 0) return 'Ended';
  if (d === 0) return 'Today';
  return `${d}d`;
}

function fmtDate(ts: any): string {
  const ms = tsToMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface CandidateRow {
  uid: string;
  name: string;
  email: string;
  coachId: string | null;
  clientId: string | null;
  enrollmentStatus: 'pending' | 'approved' | null;
  engagementStatus: 'active' | 'placed' | 'paused' | null;
  enrolledAt: any;
  programEndDate: any;
}

interface CoachOption {
  id: string;
  name: string;
  status: string;
}

interface InviteRow {
  id: string;
  candidateEmail: string;
  clientId: string | null;
  coachId: string | null;
  createdAt: any;
}

interface ImportRow {
  rowNum: number;
  candidateEmail: string;
  coachEmail: string;
  coachId: string | null;   // resolved from coaches; null if unresolved
  coachName: string | null;
  flags: string[];          // validation messages; empty = valid
}

interface ImportResult {
  enrolled: number;
  invited: number;
  failed: number;
  blobUrl: string;
  filename: string;
}

function csvEscape(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

const BLANK_ENROLL = { email: '', coachId: '', clientId: '', programEndDate: '' };

export function FirmRoster() {
  const { firmAdmin } = useAuth();
  const { firmId } = firmAdmin!;

  const [searchParams, setSearchParams] = useSearchParams();
  const clientFilter = searchParams.get('client') ?? '';
  const coachFilter = searchParams.get('coach') ?? '';
  const engagementFilter = searchParams.get('engagement') ?? '';

  const setClientFilter = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) { next.set('client', id); } else { next.delete('client'); }
    setSearchParams(next, { replace: true });
  };

  const setCoachFilter = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) { next.set('coach', id); } else { next.delete('coach'); }
    setSearchParams(next, { replace: true });
  };

  const setEngagementFilter = (val: string) => {
    const next = new URLSearchParams(searchParams);
    if (val) { next.set('engagement', val); } else { next.delete('engagement'); }
    setSearchParams(next, { replace: true });
  };

  const [candidates, setCandidates] = useState<CandidateRow[]>([]);
  const [coaches, setCoaches] = useState<CoachOption[]>([]);
  const [coachMap, setCoachMap] = useState<Record<string, string>>({});
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [inviteNotice, setInviteNotice] = useState<{ email: string; alreadyInvited: boolean } | null>(null);

  // Enroll form
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollForm, setEnrollForm] = useState(BLANK_ENROLL);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState('');

  // CSV import state
  const [importOpen, setImportOpen] = useState(false);
  const [importClientId, setImportClientId] = useState('');
  const [importProgramEndDate, setImportProgramEndDate] = useState('');
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [importParseError, setImportParseError] = useState('');
  const [importRunning, setImportRunning] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importFileName, setImportFileName] = useState('');
  const [importConfirmRows, setImportConfirmRows] = useState<ImportRow[] | null>(null);

  // Per-row action state
  const [cancellingUid, setCancellingUid] = useState<string | null>(null);
  const [assigningUid, setAssigningUid] = useState<string | null>(null);
  const [assignCoachId, setAssignCoachId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [editingClientUid, setEditingClientUid] = useState<string | null>(null);
  const [editClientId, setEditClientId] = useState('');
  const [savingClientUid, setSavingClientUid] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  const [inviteCancelError, setInviteCancelError] = useState('');
  const [confirmCancelUid, setConfirmCancelUid] = useState<string | null>(null);
  const [confirmCancelInvite, setConfirmCancelInvite] = useState<InviteRow | null>(null);

  useEffect(() => { loadData(); }, [firmId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [profilesSnap, coachesSnap, clientsSnap] = await Promise.all([
        getDocs(query(collection(db, 'profiles'), where('firmId', '==', firmId))),
        getDocs(query(collection(db, 'coaches'), where('firmId', '==', firmId))),
        getDocs(query(collection(db, 'clients'), where('firmId', '==', firmId))),
      ]);

      setClientOptions(
        clientsSnap.docs
          .map(d => ({ id: d.id, name: d.data().name ?? '' }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );

      const coachList: CoachOption[] = coachesSnap.docs.map(d => ({
        id: d.id,
        name: d.data().name ?? '',
        status: d.data().status ?? 'inactive',
      }));
      const map: Record<string, string> = {};
      for (const c of coachList) map[c.id] = c.name;
      setCoaches(coachList);
      setCoachMap(map);

      setCandidates(
        profilesSnap.docs.map(d => {
          const p = d.data();
          return {
            uid: d.id,
            name: p.name ?? p.displayName ?? '',
            email: p.authEmail ?? '',
            coachId: p.coachId ?? null,
            clientId: p.clientId ?? null,
            enrollmentStatus: p.enrollmentStatus ?? null,
            engagementStatus: p.engagementStatus ?? null,
            enrolledAt: p.enrolledAt,
            programEndDate: p.programEndDate ?? null,
          };
        })
      );

      // Separate try so a missing index doesn't break coaches/candidates.
      try {
        const invitesSnap = await getDocs(query(
          collection(db, 'firmInvites'),
          where('firmId', '==', firmId),
          where('status', '==', 'pending'),
          orderBy('createdAt', 'desc'),
        ));
        setInvites(
          invitesSnap.docs.map(d => ({
            id: d.id,
            candidateEmail: d.data().candidateEmail ?? '',
            clientId: d.data().clientId ?? null,
            coachId: d.data().coachId ?? null,
            createdAt: d.data().createdAt,
          }))
        );
      } catch {
        // Index not yet deployed — pending invites section simply stays empty.
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load roster.');
    } finally {
      setLoading(false);
    }
  };

  const activeCoaches = coaches.filter(c => c.status === 'active');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleEnroll = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollForm.email.trim() || !enrollForm.coachId) return;
    setEnrolling(true);
    setEnrollError('');
    setInviteNotice(null);
    try {
      const result = await httpsCallable<any, any>(getFunctions(), 'initiateEnrollment')({
        firmId,
        candidateEmail: enrollForm.email.trim().toLowerCase(),
        coachId: enrollForm.coachId,
        clientId: enrollForm.clientId || null,
        programEndDate: enrollForm.programEndDate || null,
      });
      const data = result.data ?? {};
      setEnrollOpen(false);
      setEnrollForm(BLANK_ENROLL);
      if (data.invited) {
        setInviteNotice({ email: data.candidateEmail, alreadyInvited: !!data.alreadyInvited });
      }
      await loadData();
    } catch (e: any) {
      const code: string = e?.code ?? '';
      if (code === 'functions/resource-exhausted') {
        setEnrollError('No seats available — contact Klarr to add seats.');
      } else {
        setEnrollError(e.message || 'Enrollment failed.');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handleCancel = async (uid: string) => {
    setCancellingUid(uid);
    setRowError(prev => ({ ...prev, [uid]: '' }));
    try {
      await httpsCallable(getFunctions(), 'cancelPendingEnrollment')({ firmId, candidateUid: uid });
      await loadData();
    } catch (e: any) {
      setRowError(prev => ({ ...prev, [uid]: e.message || 'Cancel failed.' }));
    } finally {
      setCancellingUid(null);
    }
  };

  const handleSaveClient = async (uid: string) => {
    setSavingClientUid(uid);
    setRowError(prev => ({ ...prev, [uid]: '' }));
    try {
      const newClientId = editClientId || null;
      await updateDoc(doc(db, 'profiles', uid), { clientId: newClientId });
      setCandidates(prev =>
        prev.map(c => c.uid === uid ? { ...c, clientId: newClientId } : c)
      );
      setEditingClientUid(null);
    } catch (e: any) {
      setRowError(prev => ({ ...prev, [uid]: e.message || 'Failed to update client.' }));
    } finally {
      setSavingClientUid(null);
    }
  };

  const handleAssign = async (uid: string) => {
    if (!assignCoachId) return;
    setAssigning(true);
    setRowError(prev => ({ ...prev, [uid]: '' }));
    try {
      await httpsCallable(getFunctions(), 'reassignCoach')({ candidateUid: uid, newCoachId: assignCoachId });
      setAssigningUid(null);
      setAssignCoachId('');
      await loadData();
    } catch (e: any) {
      setRowError(prev => ({ ...prev, [uid]: e.message || 'Assignment failed.' }));
    } finally {
      setAssigning(false);
    }
  };

  const handleCancelInvite = async (inv: InviteRow) => {
    setCancellingInviteId(inv.id);
    setInviteCancelError('');
    try {
      await httpsCallable(getFunctions(), 'withdrawFirmInvite')({ inviteId: inv.id });
      await deleteDoc(doc(db, 'firmInvites', inv.id));
      setInvites(prev => prev.filter(i => i.id !== inv.id));
    } catch (e: any) {
      setInviteCancelError(e.message || 'Failed to cancel invite.');
    } finally {
      setCancellingInviteId(null);
    }
  };

  const handleImportFile = (file: File) => {
    setImportParseError('');
    setImportRows([]);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = (e.target?.result as string) ?? '';
      const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')
        .map(l => l.trim()).filter(Boolean);

      if (lines.length < 2) {
        setImportParseError('CSV must have a header row and at least one data row.');
        return;
      }

      const parseCell = (raw: string) => raw.trim().replace(/^"(.*)"$/, '$1').trim();
      const headerCells = lines[0].split(',').map(c => parseCell(c).toLowerCase());
      const candidateIdx = headerCells.findIndex(h => h.includes('candidate'));
      const coachIdx = headerCells.findIndex(h => h.includes('coach'));

      if (candidateIdx === -1) {
        setImportParseError('Header must include a "candidate email" column.');
        return;
      }
      if (coachIdx === -1) {
        setImportParseError('Header must include a "coach email" column.');
        return;
      }
      if (candidateIdx === coachIdx) {
        setImportParseError('Candidate and coach columns must be distinct.');
        return;
      }

      // Build coach lookup by email — coach doc ID IS the lowercased email.
      const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const coachByEmail: Record<string, CoachOption> = {};
      for (const c of coaches) coachByEmail[c.id.toLowerCase()] = c;

      const seenCandidates = new Set<string>();
      const rows: ImportRow[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cells = lines[i].split(',').map(parseCell);
        const candidateEmail = (cells[candidateIdx] ?? '').toLowerCase();
        const coachEmail = (cells[coachIdx] ?? '').toLowerCase();
        const flags: string[] = [];

        if (!candidateEmail) {
          flags.push('Missing candidate email');
        } else if (!EMAIL_RE.test(candidateEmail)) {
          flags.push('Invalid candidate email');
        } else if (seenCandidates.has(candidateEmail)) {
          flags.push('Duplicate candidate in this file');
        } else {
          seenCandidates.add(candidateEmail);
        }

        if (!coachEmail) {
          flags.push('Missing coach email');
        } else if (!EMAIL_RE.test(coachEmail)) {
          flags.push('Invalid coach email');
        }

        let coachId: string | null = null;
        let coachName: string | null = null;
        if (coachEmail && EMAIL_RE.test(coachEmail)) {
          const match = coachByEmail[coachEmail];
          if (!match) {
            flags.push('Coach not found in this firm');
          } else if (match.status !== 'active') {
            flags.push('Coach is inactive');
          } else {
            coachId = match.id;
            coachName = match.name;
          }
        }

        rows.push({ rowNum: i, candidateEmail, coachEmail, coachId, coachName, flags });
      }

      setImportRows(rows);
    };
    reader.readAsText(file);
  };

  const handleImportConfirm = async (allRows: ImportRow[]) => {
    const validRows = allRows.filter(r => r.flags.length === 0);
    setImportRunning(true);
    setImportProgress({ current: 0, total: validRows.length });

    const callEnroll = httpsCallable<any, any>(getFunctions(), 'initiateEnrollment');
    let enrolled = 0, invited = 0, failed = 0;
    const csvLines: string[] = [
      ['Candidate Email', 'Coach Email', 'Result', 'Detail'].map(csvEscape).join(','),
    ];

    // Write pre-validation-failed rows first (not attempted via Cloud Function).
    for (const row of allRows) {
      if (row.flags.length > 0) {
        csvLines.push([row.candidateEmail, row.coachEmail, 'skipped', row.flags.join('; ')].map(csvEscape).join(','));
      }
    }

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      setImportProgress({ current: i + 1, total: validRows.length });
      try {
        const res = await callEnroll({
          firmId,
          candidateEmail: row.candidateEmail,
          coachId: row.coachId!,
          clientId: importClientId || null,
          programEndDate: importProgramEndDate || null,
        });
        const data = (res.data ?? {}) as any;
        if (data.invited) {
          invited++;
          csvLines.push([row.candidateEmail, row.coachEmail, 'invited', ''].map(csvEscape).join(','));
        } else {
          enrolled++;
          csvLines.push([row.candidateEmail, row.coachEmail, 'enrolled', ''].map(csvEscape).join(','));
        }
      } catch (e: any) {
        failed++;
        csvLines.push([row.candidateEmail, row.coachEmail, 'failed', e?.message ?? 'Unknown error'].map(csvEscape).join(','));
      }
    }

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const blobUrl = URL.createObjectURL(blob);
    const filename = `import-results-${new Date().toISOString().slice(0, 10)}.csv`;

    setImportResult({ enrolled, invited, failed, blobUrl, filename });
    setImportRunning(false);
    setImportProgress(null);
    await loadData();
  };

  const resetImport = () => {
    if (importResult?.blobUrl) URL.revokeObjectURL(importResult.blobUrl);
    setImportOpen(false);
    setImportClientId('');
    setImportProgramEndDate('');
    setImportRows([]);
    setImportParseError('');
    setImportRunning(false);
    setImportProgress(null);
    setImportResult(null);
    setImportFileName('');
  };

  // Apply filters then text search.
  let filtered = candidates;
  if (clientFilter) filtered = filtered.filter(c => c.clientId === clientFilter);
  if (coachFilter) filtered = filtered.filter(c => c.coachId === coachFilter);
  if (engagementFilter) filtered = filtered.filter(c => (c.engagementStatus ?? 'active') === engagementFilter);
  const q = search.trim().toLowerCase();
  if (q) filtered = filtered.filter(c => c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q));

  const hasFilter = !!(clientFilter || coachFilter || engagementFilter || q);

  return (
    <Layout>
      <div style={{ maxWidth: 1100 }}>
        <div className="page-header">
          <h1 className="page-title">Candidate Roster</h1>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn-secondary"
              onClick={() => {
                if (importOpen) { resetImport(); } else { setImportOpen(true); setEnrollOpen(false); }
              }}
              style={{ padding: '8px 18px', fontSize: 13 }}
            >
              {importOpen ? 'Cancel' : 'Import CSV'}
            </button>
            <button
              className="btn-primary"
              onClick={() => { setEnrollOpen(o => !o); setEnrollError(''); setEnrollForm(BLANK_ENROLL); setImportOpen(false); }}
              style={{ padding: '8px 18px', fontSize: 13 }}
            >
              {enrollOpen ? 'Cancel' : '+ Enroll Candidate'}
            </button>
          </div>
        </div>

        {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

        {inviteNotice && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              borderRadius: 6,
              background: 'var(--info-dim)',
              border: '1px solid var(--info)',
              fontSize: 13,
              color: 'var(--text-primary)',
            }}
          >
            {inviteNotice.alreadyInvited
              ? `An invite is already pending for ${inviteNotice.email}. They'll be enrolled automatically when they sign up.`
              : `Invite sent — ${inviteNotice.email} doesn't have a Klarr account yet. They'll be enrolled automatically once they sign up with this email.`}
            <button
              onClick={() => setInviteNotice(null)}
              style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, lineHeight: 1 }}
              aria-label="Dismiss"
            >×</button>
          </div>
        )}

        {/* Enroll form */}
        {enrollOpen && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Enroll Candidate
            </div>
            {enrollError && <div className="error-message" style={{ marginBottom: 14 }}>{enrollError}</div>}
            <form onSubmit={handleEnroll}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group">
                  <label className="form-label">Candidate Email *</label>
                  <input
                    className="form-input"
                    type="email"
                    required
                    value={enrollForm.email}
                    onChange={e => setEnrollForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="candidate@email.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Assign Coach *</label>
                  <select
                    className="form-input"
                    required
                    value={enrollForm.coachId}
                    onChange={e => setEnrollForm(f => ({ ...f, coachId: e.target.value }))}
                  >
                    <option value="">Select coach…</option>
                    {activeCoaches.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Client (optional)</label>
                  <select
                    className="form-input"
                    value={enrollForm.clientId}
                    onChange={e => setEnrollForm(f => ({ ...f, clientId: e.target.value }))}
                  >
                    <option value="">None</option>
                    {clientOptions.map(cl => (
                      <option key={cl.id} value={cl.id}>{cl.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Program End Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={enrollForm.programEndDate}
                    onChange={e => setEnrollForm(f => ({ ...f, programEndDate: e.target.value }))}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn-primary" disabled={enrolling}>
                  {enrolling ? 'Enrolling…' : 'Enroll'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setEnrollOpen(false); setEnrollError(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* CSV import panel */}
        {importOpen && (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 }}>
              Bulk Import
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 12, maxWidth: 660 }}>
              <div className="form-group">
                <label className="form-label">Client *</label>
                <select
                  className="form-input"
                  value={importClientId}
                  onChange={e => { setImportClientId(e.target.value); setImportRows([]); setImportParseError(''); setImportFileName(''); }}
                >
                  <option value="">Select a client…</option>
                  {clientOptions.map(cl => (
                    <option key={cl.id} value={cl.id}>{cl.name}</option>
                  ))}
                </select>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  All imported candidates will be assigned to this client.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Program End Date</label>
                <input
                  className="form-input"
                  type="date"
                  value={importProgramEndDate}
                  onChange={e => setImportProgramEndDate(e.target.value)}
                />
              </div>
            </div>
            <div style={{ marginBottom: 16, maxWidth: 400 }}>
              <div className="form-group">
                <label className="form-label">CSV File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) { setImportFileName(file.name); handleImportFile(file); }
                    e.target.value = '';
                  }}
                />
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={!importClientId}
                    style={{ padding: '6px 14px', fontSize: 13 }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose CSV File
                  </button>
                  {importFileName && (
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {importFileName} selected
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Two columns required: <code>candidate email</code>, <code>coach email</code>. Header row required.
                </div>
              </div>
            </div>

            {importParseError && (
              <div className="error-message" style={{ marginBottom: 16 }}>{importParseError}</div>
            )}

            {importRunning && importProgress && (
              <div style={{ marginBottom: 16, fontSize: 14, color: 'var(--text-secondary)' }}>
                Enrolling {importProgress.current} of {importProgress.total}…
              </div>
            )}

            {importResult && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                  Import complete —{' '}
                  <span style={{ color: 'var(--accent-text)' }}>{importResult.enrolled} enrolled</span>
                  {importResult.invited > 0 && (
                    <>, <span style={{ color: 'var(--text-secondary)' }}>{importResult.invited} invited</span></>
                  )}
                  {importResult.failed > 0 && (
                    <>, <span style={{ color: 'var(--error)' }}>{importResult.failed} failed</span></>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <a
                    href={importResult.blobUrl}
                    download={importResult.filename}
                    className="btn-secondary"
                    style={{ padding: '6px 14px', fontSize: 13, textDecoration: 'none', display: 'inline-block' }}
                  >
                    Download Results CSV
                  </a>
                  <button
                    className="btn-secondary"
                    style={{ padding: '6px 14px', fontSize: 13 }}
                    onClick={resetImport}
                  >
                    New Import
                  </button>
                </div>
              </div>
            )}

            {!importRunning && !importResult && importRows.length > 0 && (() => {
              const validRows = importRows.filter(r => r.flags.length === 0);
              const flaggedCount = importRows.length - validRows.length;
              return (
                <>
                  <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 13, alignItems: 'center' }}>
                    <span style={{ color: 'var(--accent-text)', fontWeight: 500 }}>{validRows.length} valid</span>
                    {flaggedCount > 0 && (
                      <span style={{ color: 'var(--error)', fontWeight: 500 }}>{flaggedCount} flagged</span>
                    )}
                  </div>

                  <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 16, border: '1px solid var(--border)', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-secondary)' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', width: 36 }}>#</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>Candidate Email</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>Coach Email</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>Resolved Coach</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importRows.map(row => (
                          <tr
                            key={row.rowNum}
                            style={{ background: row.flags.length > 0 ? 'var(--danger-row)' : undefined, borderBottom: '1px solid var(--border)' }}
                          >
                            <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{row.rowNum}</td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                              {row.candidateEmail || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                              {row.coachEmail || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 12px', fontSize: 13 }}>
                              {row.coachName ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {row.flags.length === 0 ? (
                                <span style={{ fontSize: 12, color: 'var(--accent-text)', fontWeight: 500 }}>✓ Valid</span>
                              ) : (
                                row.flags.map((f, i) => (
                                  <div key={i} style={{ fontSize: 12, color: 'var(--error)' }}>{f}</div>
                                ))
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <button
                    className="btn-primary"
                    disabled={validRows.length === 0}
                    onClick={() => setImportConfirmRows(importRows)}
                  >
                    Confirm Import ({validRows.length} candidate{validRows.length !== 1 ? 's' : ''})
                  </button>
                </>
              );
            })()}
          </div>
        )}

        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="form-input"
              type="search"
              placeholder="Search by name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: 300, margin: 0 }}
            />
            {clientOptions.length > 0 && (
              <select
                className="form-input"
                value={clientFilter}
                onChange={e => setClientFilter(e.target.value)}
                style={{ maxWidth: 220, margin: 0 }}
              >
                <option value="">All Clients</option>
                {clientOptions.map(cl => (
                  <option key={cl.id} value={cl.id}>{cl.name}</option>
                ))}
              </select>
            )}
            {activeCoaches.length > 0 && (
              <select
                className="form-input"
                value={coachFilter}
                onChange={e => setCoachFilter(e.target.value)}
                style={{ maxWidth: 220, margin: 0 }}
              >
                <option value="">All Coaches</option>
                {activeCoaches.map(co => (
                  <option key={co.id} value={co.id}>{co.name}</option>
                ))}
              </select>
            )}
            <select
              className="form-input"
              value={engagementFilter}
              onChange={e => setEngagementFilter(e.target.value)}
              style={{ maxWidth: 180, margin: 0 }}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="placed">Placed</option>
              <option value="paused">Paused</option>
            </select>
          </div>

          {loading ? (
            <div className="loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 14 }}>
              {hasFilter ? 'No candidates match the current filters.' : 'No candidates enrolled yet.'}
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Coach</th>
                    <th>Status</th>
                    <th>Engagement</th>
                    <th>Enrolled</th>
                    <th>Days Left</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(c => (
                    <React.Fragment key={c.uid}>
                      <tr>
                        <td>
                          <div style={{ fontWeight: 500 }}>{c.name || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace' }}>
                            {c.email || '—'}
                          </div>
                        </td>
                        <td style={{ color: c.coachId ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                          {c.coachId ? (coachMap[c.coachId] ?? c.coachId) : '—'}
                        </td>
                        <td>
                          <StatusBadge status={c.enrollmentStatus} />
                        </td>
                        <td>
                          <EngagementBadge status={c.engagementStatus} />
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {fmtDate(c.enrolledAt)}
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                          {fmtDaysLeft(c.programEndDate) ?? ''}
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                            {c.enrollmentStatus === 'pending' && (
                              <button
                                className="btn-danger"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                disabled={cancellingUid === c.uid}
                                onClick={() => setConfirmCancelUid(c.uid)}
                              >
                                {cancellingUid === c.uid ? 'Cancelling…' : 'Cancel'}
                              </button>
                            )}
                            {c.coachId === null && c.enrollmentStatus === 'approved' && assigningUid !== c.uid && (
                              <button
                                className="btn-secondary"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => { setAssigningUid(c.uid); setAssignCoachId(''); setEditingClientUid(null); }}
                              >
                                Assign Coach
                              </button>
                            )}
                            {c.coachId !== null && c.enrollmentStatus === 'approved' && assigningUid !== c.uid && (
                              <button
                                className="btn-secondary"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => { setAssigningUid(c.uid); setAssignCoachId(''); setEditingClientUid(null); }}
                              >
                                Change Coach
                              </button>
                            )}
                            {clientOptions.length > 0 && editingClientUid !== c.uid && (
                              <button
                                className="btn-secondary"
                                style={{ padding: '4px 12px', fontSize: 12 }}
                                onClick={() => { setEditingClientUid(c.uid); setEditClientId(c.clientId ?? ''); setAssigningUid(null); }}
                              >
                                Edit client
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {/* Inline assign-coach panel */}
                      {assigningUid === c.uid && (
                        <tr>
                          <td colSpan={7} style={{ background: 'var(--bg-secondary)', padding: '12px 16px' }}>
                            {rowError[c.uid] && (
                              <div className="error-message" style={{ marginBottom: 10, fontSize: 13 }}>
                                {rowError[c.uid]}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <select
                                className="form-input"
                                value={assignCoachId}
                                onChange={e => setAssignCoachId(e.target.value)}
                                style={{ maxWidth: 260, margin: 0 }}
                              >
                                <option value="">Select coach…</option>
                                {activeCoaches.map(co => (
                                  <option key={co.id} value={co.id}>{co.name}</option>
                                ))}
                              </select>
                              <button
                                className="btn-primary"
                                style={{ padding: '8px 16px', fontSize: 13 }}
                                disabled={assigning || !assignCoachId}
                                onClick={() => handleAssign(c.uid)}
                              >
                                {assigning ? 'Assigning…' : 'Assign'}
                              </button>
                              <button
                                className="btn-secondary"
                                style={{ padding: '8px 16px', fontSize: 13 }}
                                onClick={() => { setAssigningUid(null); setAssignCoachId(''); }}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Inline edit-client panel */}
                      {editingClientUid === c.uid && (
                        <tr>
                          <td colSpan={7} style={{ background: 'var(--bg-secondary)', padding: '12px 16px' }}>
                            {rowError[c.uid] && (
                              <div className="error-message" style={{ marginBottom: 10, fontSize: 13 }}>
                                {rowError[c.uid]}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <select
                                className="form-input"
                                value={editClientId}
                                onChange={e => setEditClientId(e.target.value)}
                                style={{ maxWidth: 260, margin: 0 }}
                              >
                                <option value="">No client</option>
                                {clientOptions.map(cl => (
                                  <option key={cl.id} value={cl.id}>{cl.name}</option>
                                ))}
                              </select>
                              <button
                                className="btn-primary"
                                style={{ padding: '8px 16px', fontSize: 13 }}
                                disabled={savingClientUid === c.uid}
                                onClick={() => handleSaveClient(c.uid)}
                              >
                                {savingClientUid === c.uid ? 'Saving…' : 'Save'}
                              </button>
                              <button
                                className="btn-secondary"
                                style={{ padding: '8px 16px', fontSize: 13 }}
                                onClick={() => setEditingClientUid(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* Row-level error (cancel / non-assign / non-edit-client errors) */}
                      {rowError[c.uid] && assigningUid !== c.uid && editingClientUid !== c.uid && (
                        <tr>
                          <td colSpan={7} style={{ padding: '0 16px 12px' }}>
                            <div className="error-message" style={{ fontSize: 13 }}>{rowError[c.uid]}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
          {filtered.length} candidate{filtered.length !== 1 ? 's' : ''}{hasFilter ? ' matched' : ' enrolled'}
        </div>

        {(() => {
          let filteredInvites = clientFilter
            ? invites.filter(inv => inv.clientId === clientFilter)
            : invites;
          if (coachFilter) filteredInvites = filteredInvites.filter(inv => inv.coachId === coachFilter);
          if (filteredInvites.length === 0) return null;
          return (
            <div style={{ marginTop: 32 }}>
              <div style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                marginBottom: 10,
              }}>
                Pending Invites ({filteredInvites.length})
              </div>
              <div className="card" style={{ padding: 0 }}>
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Coach</th>
                        <th>Invited</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvites.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{inv.candidateEmail}</td>
                          <td style={{ fontSize: 13, color: inv.coachId ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                            {inv.coachId ? (coachMap[inv.coachId] ?? inv.coachId) : '—'}
                          </td>
                          <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fmtDate(inv.createdAt)}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="btn-danger"
                              style={{ padding: '4px 12px', fontSize: 12 }}
                              disabled={cancellingInviteId === inv.id}
                              onClick={() => setConfirmCancelInvite(inv)}
                            >
                              {cancellingInviteId === inv.id ? 'Cancelling…' : 'Cancel'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {inviteCancelError && (
                <div className="error-message" style={{ marginTop: 10, fontSize: 13 }}>{inviteCancelError}</div>
              )}
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                These candidates will be enrolled automatically when they sign up with this email address.
              </div>
            </div>
          );
        })()}
      </div>

      {importConfirmRows !== null && (() => {
        const n = importConfirmRows.filter(r => r.flags.length === 0).length;
        return (
          <ConfirmModal
            title="Confirm Import"
            message={`This will attempt to enroll ${n} candidate${n !== 1 ? 's' : ''} and permanently consume up to ${n} seat${n !== 1 ? 's' : ''}, even for rows that fail partway through others.`}
            confirmLabel="Confirm Import"
            onConfirm={() => { setImportConfirmRows(null); handleImportConfirm(importConfirmRows); }}
            onCancel={() => setImportConfirmRows(null)}
          />
        );
      })()}
      {confirmCancelUid !== null && (
        <ConfirmModal
          title="Cancel Enrollment"
          message="Cancel this enrollment? The candidate will be notified and their seat will not be returned."
          confirmLabel="Cancel Enrollment"
          onConfirm={() => { setConfirmCancelUid(null); handleCancel(confirmCancelUid); }}
          onCancel={() => setConfirmCancelUid(null)}
        />
      )}
      {confirmCancelInvite !== null && (
        <ConfirmModal
          title="Cancel Invitation"
          message={`Cancel the invitation to ${confirmCancelInvite.candidateEmail}? They will receive a brief notification email.`}
          confirmLabel="Cancel Invitation"
          onConfirm={() => { setConfirmCancelInvite(null); handleCancelInvite(confirmCancelInvite); }}
          onCancel={() => setConfirmCancelInvite(null)}
        />
      )}
    </Layout>
  );
}

function EngagementBadge({ status }: { status: 'active' | 'placed' | 'paused' | null }) {
  const s = status ?? 'active';
  if (s === 'placed') return <span className="badge badge-active">Placed</span>;
  if (s === 'paused') return (
    <span className="badge" style={{ background: 'var(--warning-dim)', color: 'var(--warning)' }}>Paused</span>
  );
  return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>Active</span>;
}

function StatusBadge({ status }: { status: 'pending' | 'approved' | null }) {
  if (status === 'approved') {
    return <span className="badge badge-active">Approved</span>;
  }
  if (status === 'pending') {
    return (
      <span className="badge" style={{ background: 'var(--warning-dim)', color: 'var(--warning)' }}>
        Pending
      </span>
    );
  }
  return <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>;
}
